// ─── Module inventory — feuille d'édition d'un ÉLÉMENT ────────────
// Modèle unifié : un élément = un regroupement (nom, emoji, catégorie) + N
// ARTICLES. Tout ce qui décrit l'objet physique (quantité, seuil, photos,
// code-barres, emplacement) vit sur l'article. Un élément à 1 article s'édite
// « à plat » ; dès qu'il y en a plusieurs, ils s'affichent en accordéons.
//
// Sous-modules mobilisés : inv-objets, inv-photos, inv-scan, inv-referentiel.
import React, { useMemo, useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator,
} from 'react-native';
import {
  useUI, useT, useAuth, useEspace, Feuille, SelecteurEmoji, SelecteurDate, confirmerSuppression,
} from '@mop/core';
import { nouvelId, aplatirArbre, articlesDe, seuilItem } from './services/inventaires';
import { resoudreCode, codeBarresValide } from './services/produits';
import ScannerCodeBarres from './ui/ScannerCodeBarres';
import PhotosArticle from './ui/PhotosArticle';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const nombre = (v) => String(v ?? '').replace(/[^0-9]/g, '');

const artVide = () => ({
  id: nouvelId('a'), nom: '', qte: '1', seuil: '0', photos: [], codeBarres: null,
  unique: false, consommable: false, prix: '', dateAchat: '', emplacementId: null, vals: {},
  auteur: '', editeur: '', annee: '', pages: '',   // métadonnées livre (scan ISBN)
});

// État initial d'édition depuis un élément existant (ou vide à la création).
export function etatDepuisItem(it, catParDefaut = null) {
  if (!it) {
    return { id: null, nom: '', emoji: '', catId: catParDefaut, seuil: '0', articles: [artVide()] };
  }
  return {
    id: it.id, nom: it.nom || '', emoji: it.emoji || '', catId: it.catId ?? null,
    seuil: String(seuilItem(it)),
    articles: articlesDe(it).map(a => ({
      id: a.id || nouvelId('a'), nom: a.nom || '', qte: String(a.qte ?? 0), seuil: String(a.seuil ?? 0),
      photos: Array.isArray(a.photos) ? a.photos : (a.photo ? [a.photo] : []),
      codeBarres: a.codeBarres || null, unique: !!a.unique, consommable: !!a.consommable,
      prix: a.prix != null ? String(a.prix) : '', dateAchat: a.dateAchat || '',
      auteur: a.auteur || '', editeur: a.editeur || '',
      annee: a.annee != null ? String(a.annee) : '', pages: a.pages != null ? String(a.pages) : '',
      emplacementId: a.emplacementId || null, vals: a.vals || {},
      // On préserve l'historique de comptage — non éditable ici, mais à ne pas perdre.
      dernierPointage: a.dernierPointage || null, historique: a.historique || null,
    })),
  };
}

// Sérialise l'état vers les champs Firestore d'un élément.
export function itemDepuisEtat(e) {
  return {
    nom: e.nom.trim(), emoji: e.emoji || '', catId: e.catId ?? null,
    seuil: Number(e.seuil) || 0,
    articles: e.articles.map(a => ({
      id: a.id, nom: (a.nom || '').trim() || e.nom.trim(),
      qte: Number(a.qte) || 0, seuil: Number(a.seuil) || 0,
      photos: a.photos || [], codeBarres: a.codeBarres || null,
      unique: !!a.unique, consommable: !!a.consommable,
      prix: a.prix !== '' && a.prix != null ? Number(String(a.prix).replace(',', '.')) || 0 : null,
      dateAchat: (a.dateAchat || '').trim() || null,
      auteur: (a.auteur || '').trim() || null, editeur: (a.editeur || '').trim() || null,
      annee: (a.annee || '').toString().trim() || null,
      pages: a.pages !== '' && a.pages != null ? Number(a.pages) || null : null,
      emplacementId: a.emplacementId || null, vals: a.vals || {},
      ...(a.dernierPointage ? { dernierPointage: a.dernierPointage } : {}),
      ...(a.historique ? { historique: a.historique } : {}),
    })),
  };
}

export default function FeuilleElement({
  visible, etat, setEtat, onEnregistrer, onFermer, modifie, busy,
  arbre = [], emplacements = [], proprietes = [], dossierPhotos,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { tenantId } = useEspace();

  const [scan, setScan]     = useState(null);   // index d'article en cours de scan
  const [ouvert, setOuvert] = useState(0);      // accordéon d'article ouvert
  const [pickDate, setPickDate] = useState(null); // { i, pid } propriété date en cours
  const [look, setLook]     = useState('');     // message de recherche produit
  const [busyScan, setBS]   = useState(false);

  const cats = useMemo(() => aplatirArbre(arbre), [arbre]);
  const emps = useMemo(() => aplatirArbre(emplacements), [emplacements]);
  const arts = etat?.articles || [];
  const mono = arts.length === 1;

  const majArt = (i, patch) => setEtat(e => ({
    ...e, articles: e.articles.map((a, j) => (j === i ? { ...a, ...patch } : a)),
  }));
  const ajouterArticle = () => setEtat(e => {
    const suivant = e.articles.length;
    setOuvert(suivant);
    return {
      ...e,
      articles: e.articles.concat([{
        id: nouvelId('a'), nom: '', qte: '1', seuil: '0', photos: [],
        codeBarres: null, unique: false, emplacementId: null,
      }]),
    };
  });
  const retirerArticle = (i) => {
    if (arts.length <= 1) return;                  // un élément a toujours ≥ 1 article
    confirmerSuppression(t('inventaire.retirerArticleTitre'), t('inventaire.retirerArticleMsg', { nom: arts[i].nom || t('inventaire.sansNom') }),
      () => setEtat(e => ({ ...e, articles: e.articles.filter((_, j) => j !== i) })));
  };

  // Scan → référentiel de l'espace, puis Open Food Facts en repli.
  async function traiterCode(i, code) {
    setScan(null);
    const c = String(code || '').trim();
    if (!codeBarresValide(c)) { setLook(t('inventaire.codeInvalideMsg')); return; }
    majArt(i, { codeBarres: c });
    setBS(true); setLook(t('inventaire.rechercheProduit'));
    try {
      const { fiche, origine, raison } = await resoudreCode(tenantId, c, { uid: user?.uid });
      if (!fiche) {
        setLook(raison === 'inconnu'
          ? t('inventaire.codeInconnuMsg', { c })
          : t('inventaire.codeEnregistreMsg', { c }));
      } else {
        const estLivre = fiche.source === 'google-books' || fiche.source === 'open-library';
        const patch = { nom: (arts[i].nom || '').trim() || fiche.nom || '' };
        // La photo (couverture ou produit) est une URL publique : on la référence.
        if (fiche.photoUrl && !(arts[i].photos || []).length) patch.photos = [{ url: fiche.photoUrl }];
        // Métadonnées livre : auteur / éditeur / année / pages.
        if (fiche.auteur)  patch.auteur  = fiche.auteur;
        if (fiche.editeur) patch.editeur = fiche.editeur;
        if (fiche.annee)   patch.annee   = String(fiche.annee);
        if (fiche.pages)   patch.pages   = String(fiche.pages);
        majArt(i, patch);
        setEtat(e => ({ ...e, nom: e.nom.trim() || fiche.nom || '' }));
        const src = { 'local': `📇 ${t('inventaire.sourceReferentiel')}`, 'google-books': '📚 Google Books',
          'open-library': '📖 Open Library', 'off': '🌍 Open Food Facts' }[origine] || `🌍 ${t('inventaire.sourcePublique')}`;
        const sous = estLivre ? [fiche.auteur, fiche.editeur].filter(Boolean).join(' · ') : fiche.marque;
        setLook(`${src} : ${fiche.nom}${sous ? ` — ${sous}` : ''}`);
      }
    } catch (e) {
      setLook(t('inventaire.codeReseauMsg'));
    }
    setBS(false);
  }

  if (!visible) return null;

  return (
   <>
    <Feuille visible titre={etat?.id ? t('inventaire.modifierElement') : t('inventaire.nouvelElement')} emoji="🧷"
      hauteur={0.86} sansScroll onFermer={onFermer}
      action={{ icone: etat?.id ? '💾' : '＋', onPress: onEnregistrer, disabled: !modifie || busy, label: t('commun.enregistrer') }}>
          <ScrollView contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled">
            <Text style={s.lbl}>{t('listes.nom')}</Text>
            <TextInput style={s.input} value={etat?.nom || ''} autoFocus={!etat?.id}
              onChangeText={(v) => setEtat(e => ({ ...e, nom: v }))}
              placeholder={t('inventaire.phNomElementEx')} placeholderTextColor={UI.faint} />

            {cats.length > 0 && (
              <>
                <Text style={s.lbl}>{t('finances.niv_categorie')}</Text>
                <View style={s.puces}>
                  <TouchableOpacity style={[s.chip, !etat?.catId && s.chipOn]}
                    onPress={() => setEtat(e => ({ ...e, catId: null }))}>
                    <Text style={[s.chipTxt, !etat?.catId && s.chipTxtOn]}>{t('inventaire.sansCategorie')}</Text>
                  </TouchableOpacity>
                  {cats.map(c => (
                    <TouchableOpacity key={c.id} style={[s.chip, etat?.catId === c.id && s.chipOn]}
                      onPress={() => setEtat(e => ({ ...e, catId: c.id }))}>
                      <Text style={[s.chipTxt, etat?.catId === c.id && s.chipTxtOn]}>
                        {'· '.repeat(c.profondeur)}{c.emoji || ''} {c.nom}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}

            <Text style={s.lbl}>{t('inventaire.seuilAlerteElement')}</Text>
            <TextInput style={[s.input, { width: 110 }]} value={etat?.seuil || ''} keyboardType="number-pad"
              onChangeText={(v) => setEtat(e => ({ ...e, seuil: nombre(v) }))} />
            <Text style={s.aide}>
              {t('inventaire.seuilAlerteAide')}
            </Text>

            {/* ── Articles ─────────────────────────────────────────── */}
            <View style={s.sectTete}>
              <Text style={s.sect}>{mono ? t('plans.type_article') : t('inventaire.articlesCount', { n: arts.length })}</Text>
              <TouchableOpacity onPress={ajouterArticle} hitSlop={HIT} accessibilityLabel={t('inventaire.ajouterArticle')}>
                <Text style={s.lienAjout}>＋</Text>
              </TouchableOpacity>
            </View>
            {!mono && (
              <Text style={s.aide}>
                {t('inventaire.articlesMultiAide')}
              </Text>
            )}

            {arts.map((a, i) => {
              const plie = !mono && ouvert !== i;
              return (
                <View key={a.id} style={s.art}>
                  {!mono && (
                    <TouchableOpacity style={s.artTete} onPress={() => setOuvert(o => (o === i ? -1 : i))}>
                      <Text style={s.artNom} numberOfLines={1}>
                        {a.nom?.trim() || t('inventaire.articleN', { n: i + 1 })}
                      </Text>
                      <Text style={s.artQte}>{a.qte || 0}</Text>
                      {/* ✕ et non 🗑️ : dérogation assumée à la règle app-wide
                          (on supprime au swipe), car le swipe de la feuille
                          sert déjà à retirer l'élément entier. Même forme que
                          les plages horaires d'un contact. Le bloc est déjà
                          conditionné par `!mono` : avec un seul article, rien
                          à retirer ligne par ligne. */}
                      <TouchableOpacity onPress={() => retirerArticle(i)} hitSlop={HIT}
                        accessibilityLabel={t('commun.supprimer')}>
                        <Text style={s.artSuppr}>✕</Text>
                      </TouchableOpacity>
                      <Text style={s.chev}>{plie ? '›' : '▾'}</Text>
                    </TouchableOpacity>
                  )}
                  {!plie && (
                    <View style={mono ? null : s.artCorps}>
                      {!mono && (
                        <>
                          <Text style={s.lbl}>{t('inventaire.nomArticle')}</Text>
                          <TextInput style={s.input} value={a.nom}
                            onChangeText={(v) => majArt(i, { nom: v })}
                            placeholder={etat?.nom || t('inventaire.commeElement')} placeholderTextColor={UI.faint} />
                        </>
                      )}

                      <View style={{ flexDirection: 'row', gap: 12 }}>
                        <View style={{ flex: 1 }}>
                          <Text style={s.lbl}>{t('listes.quantite')}</Text>
                          <TextInput style={s.input} value={a.qte} keyboardType="number-pad"
                            onChangeText={(v) => majArt(i, { qte: nombre(v) })} />
                        </View>
                        {!mono && (
                          <View style={{ flex: 1 }}>
                            <Text style={s.lbl}>{t('inventaire.seuilArticle')}</Text>
                            <TextInput style={s.input} value={a.seuil} keyboardType="number-pad"
                              onChangeText={(v) => majArt(i, { seuil: nombre(v) })} />
                          </View>
                        )}
                      </View>

                      <Text style={s.lbl}>{t('inventaire.codeBarres')}</Text>
                      <View style={s.codeRow}>
                        <TextInput style={[s.input, { flex: 1 }]} value={a.codeBarres || ''} keyboardType="number-pad"
                          onChangeText={(v) => majArt(i, { codeBarres: nombre(v) || null })}
                          placeholder={t('inventaire.phCodeBarres')} placeholderTextColor={UI.faint} />
                        <TouchableOpacity style={s.scanBtn} onPress={() => { setLook(''); setScan(i); }}>
                          <Text style={s.scanIc}>📷</Text>
                        </TouchableOpacity>
                        {!!a.codeBarres && (
                          <TouchableOpacity style={s.scanBtn} disabled={busyScan}
                            onPress={() => traiterCode(i, a.codeBarres)}>
                            <Text style={s.scanIc}>🔎</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                      {busyScan && <ActivityIndicator color={UI.accent} style={{ marginTop: 8 }} />}
                      {!!look && <Text style={s.look}>{look}</Text>}

                      {emps.length > 0 && (
                        <>
                          <Text style={s.lbl}>{t('inventaire.rangeDans')}</Text>
                          <View style={s.puces}>
                            <TouchableOpacity style={[s.chip, !a.emplacementId && s.chipOn]}
                              onPress={() => majArt(i, { emplacementId: null })}>
                              <Text style={[s.chipTxt, !a.emplacementId && s.chipTxtOn]}>{t('inventaire.nullePart')}</Text>
                            </TouchableOpacity>
                            {emps.map(o => (
                              <TouchableOpacity key={o.id} style={[s.chip, a.emplacementId === o.id && s.chipOn]}
                                onPress={() => majArt(i, { emplacementId: o.id })}>
                                <Text style={[s.chipTxt, a.emplacementId === o.id && s.chipTxtOn]}>
                                  {'· '.repeat(o.profondeur)}{o.emoji || '📍'} {o.nom}
                                </Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </>
                      )}

                      {/* Détails livre (auto-remplis au scan ISBN, éditables). */}
                      {(a.auteur || a.editeur || a.annee || a.pages) && (
                        <>
                          <View style={{ flexDirection: 'row', gap: 12 }}>
                            <View style={{ flex: 1 }}>
                              <Text style={s.lbl}>{t('inventaire.auteur')}</Text>
                              <TextInput style={s.input} value={a.auteur}
                                onChangeText={(v) => majArt(i, { auteur: v })} placeholder="—" placeholderTextColor={UI.faint} />
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={s.lbl}>{t('inventaire.editeur')}</Text>
                              <TextInput style={s.input} value={a.editeur}
                                onChangeText={(v) => majArt(i, { editeur: v })} placeholder="—" placeholderTextColor={UI.faint} />
                            </View>
                          </View>
                          <View style={{ flexDirection: 'row', gap: 12 }}>
                            <View style={{ flex: 1 }}>
                              <Text style={s.lbl}>{t('inventaire.parution')}</Text>
                              <TextInput style={s.input} value={a.annee} keyboardType="number-pad"
                                onChangeText={(v) => majArt(i, { annee: nombre(v) })} placeholder={t('inventaire.phAnnee')} placeholderTextColor={UI.faint} />
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={s.lbl}>{t('inventaire.pages')}</Text>
                              <TextInput style={s.input} value={a.pages} keyboardType="number-pad"
                                onChangeText={(v) => majArt(i, { pages: nombre(v) })} placeholder="—" placeholderTextColor={UI.faint} />
                            </View>
                          </View>
                        </>
                      )}

                      {/* Achat : prix + date (comme MoPCal). */}
                      <View style={{ flexDirection: 'row', gap: 12 }}>
                        <View style={{ flex: 1 }}>
                          <Text style={s.lbl}>{t('inventaire.prixAchat')}</Text>
                          <TextInput style={s.input} value={a.prix} keyboardType="decimal-pad"
                            placeholder="—" placeholderTextColor={UI.faint}
                            onChangeText={(v) => majArt(i, { prix: v.replace(/[^0-9.,]/g, '') })} />
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={s.lbl}>{t('inventaire.dateAchat')}</Text>
                          <TextInput style={s.input} value={a.dateAchat}
                            placeholder={t('inventaire.phDateJJMMAAAA')} placeholderTextColor={UI.faint}
                            onChangeText={(v) => majArt(i, { dateAchat: v })} />
                        </View>
                      </View>

                      <Text style={s.lbl}>{t('listes.photos')}</Text>
                      <PhotosArticle photos={a.photos} dossier={dossierPhotos}
                        onChange={(p) => majArt(i, { photos: p })} />

                      {/* Propriétés sur mesure de l'inventaire (texte/nombre/date/liste). */}
                      {proprietes.length > 0 && (
                        <>
                          <Text style={s.lbl}>{t('inventaire.proprietes')}</Text>
                          {proprietes.map(p => (
                            <View key={p.id} style={{ marginBottom: 8 }}>
                              <Text style={s.propLbl}>{p.nom}</Text>
                              {p.type === 'liste' ? (
                                <View style={s.puces}>
                                  {(p.options || []).map(o => {
                                    const on = (a.vals || {})[p.id] === o;
                                    return (
                                      <TouchableOpacity key={o} style={[s.chip, on && s.chipOn]}
                                        onPress={() => majArt(i, { vals: { ...(a.vals || {}), [p.id]: on ? '' : o } })}>
                                        <Text style={[s.chipTxt, on && s.chipTxtOn]}>{o}</Text>
                                      </TouchableOpacity>
                                    );
                                  })}
                                </View>
                              ) : p.type === 'date' ? (
                                <TouchableOpacity style={s.champBtn} onPress={() => setPickDate({ i, pid: p.id })}>
                                  <Text style={[s.champVal, !(a.vals || {})[p.id] && s.champVide]}>📅 {(a.vals || {})[p.id]
                                    ? `${(a.vals[p.id]).slice(8, 10)}/${(a.vals[p.id]).slice(5, 7)}/${(a.vals[p.id]).slice(0, 4)}`
                                    : t('ft.choisir')}</Text>
                                </TouchableOpacity>
                              ) : (
                                <TextInput style={s.input} value={(a.vals || {})[p.id] || ''}
                                  keyboardType={p.type === 'nombre' ? 'decimal-pad' : 'default'}
                                  placeholderTextColor={UI.faint}
                                  onChangeText={(v) => majArt(i, { vals: { ...(a.vals || {}), [p.id]: v } })} />
                              )}
                            </View>
                          ))}
                        </>
                      )}

                      <TouchableOpacity style={s.uniqueRow} onPress={() => majArt(i, { unique: !a.unique })}>
                        <Text style={s.uniqueCase}>{a.unique ? '☑️' : '⬜'}</Text>
                        <Text style={s.uniqueTxt}>{t('inventaire.exemplaireUniqueInline')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={s.uniqueRow} onPress={() => majArt(i, { consommable: !a.consommable })}>
                        <Text style={s.uniqueCase}>{a.consommable ? '☑️' : '⬜'}</Text>
                        <Text style={s.uniqueTxt}>{t('inventaire.consommableInline')}</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}

            <Text style={s.lbl}>{t('inventaire.iconeElement')}</Text>
            <SelecteurEmoji inline avecRecherche valeur={etat?.emoji || '📦'}
              onChange={(e) => setEtat(x => ({ ...x, emoji: e }))} />
            <View style={{ height: 30 }} />
          </ScrollView>

          {/* Calque de scan DANS la feuille : jamais deux <Modal> natives empilées. */}
          <ScannerCodeBarres visible={scan != null} mode="barcode"
            onClose={() => setScan(null)} onScan={(code) => traiterCode(scan, code)} />
    </Feuille>
    <SelecteurDate visible={!!pickDate} titre={t('commun.choisirDate')}
      valeur={pickDate ? ((etat?.articles?.[pickDate.i]?.vals || {})[pickDate.pid] || '') : ''}
      avecEffacer
      onChange={(dk) => { if (pickDate) majArt(pickDate.i, { vals: { ...(etat.articles[pickDate.i].vals || {}), [pickDate.pid]: dk } }); }}
      onFermer={() => setPickDate(null)} />
   </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '86%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  corps:   { padding: 16 },
  hIc:     { fontSize: 19, color: UI.accent },
  lbl:     { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  propLbl: { fontSize: 11.5, color: UI.faint, fontWeight: '600', marginBottom: 5 },
  input:   { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11,
             fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  aide:    { fontSize: 11.5, color: UI.faint, marginTop: 7, lineHeight: 16 },
  puces:   { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  chip:      { borderWidth: 1, borderColor: UI.border, borderRadius: 9, paddingHorizontal: 11, paddingVertical: 6,
               backgroundColor: UI.card },
  chipOn:    { borderColor: UI.accent, backgroundColor: UI.accentTint },
  chipTxt:   { fontSize: 12.5, color: UI.muted, fontWeight: '600' },
  chipTxtOn: { color: UI.accent },
  sectTete:  { flexDirection: 'row', alignItems: 'flex-end', marginTop: 22, marginBottom: 4 },
  sect:      { flex: 1, fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.4 },
  lienAjout: { fontSize: 18, color: UI.accent, fontWeight: '700' },
  art:      { marginTop: 10, borderWidth: 1, borderColor: UI.border, borderRadius: 12, backgroundColor: UI.card,
              overflow: 'hidden' },
  artTete:  { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12 },
  artNom:   { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  artQte:   { fontSize: 14, fontWeight: '800', color: UI.accent },
  artSuppr: { fontSize: 15, color: UI.muted, fontWeight: '700', paddingHorizontal: 2 },
  chev:     { fontSize: 18, color: UI.faint, fontWeight: '300' },
  artCorps: { paddingHorizontal: 12, paddingBottom: 14, borderTopWidth: 0.5, borderTopColor: UI.border },
  codeRow:  { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scanBtn:  { width: 44, height: 44, borderRadius: 10, borderWidth: 1, borderColor: UI.border,
              alignItems: 'center', justifyContent: 'center', backgroundColor: UI.champ },
  scanIc:   { fontSize: 18 },
  look:     { fontSize: 12, color: UI.accent, marginTop: 8, fontWeight: '600' },
  uniqueRow:  { flexDirection: 'row', alignItems: 'center', gap: 9, marginTop: 16 },
  uniqueCase: { fontSize: 17 },
  uniqueTxt:  { flex: 1, fontSize: 12.5, color: UI.muted },
});
